/** Data of the selected project the worker downloads before the project opens. */
export enum ProjectDataKey {
  issues = 'issues',
  templates = 'templates',
  inspections = 'inspections',
  companies = 'companies',
  contracts = 'contracts',
  users = 'users',
  sites = 'sites',
  levels = 'levels',
  documentations = 'documentations',
  directories = 'directories',
  issueForms = 'issueForms',
  visibleFields = 'visibleFields',
}

export const PROJECT_DATA_KEYS: readonly ProjectDataKey[] = Object.values(ProjectDataKey);

/** Inspections and their templates: a standard user has none, so nobody waits for them. */
export const INSPECTION_DATA_KEYS: readonly ProjectDataKey[] = [ProjectDataKey.templates, ProjectDataKey.inspections];

export function isInspectionDataKey(key: ProjectDataKey): boolean {
  return INSPECTION_DATA_KEYS.includes(key);
}

/** Keys the page waits for: inspection data only for roles that have it. */
export function getProjectDataKeysToWaitFor(waitsForInspectionData: boolean): ProjectDataKey[] {
  return PROJECT_DATA_KEYS.filter((key) => waitsForInspectionData || !isInspectionDataKey(key));
}

/** Where the download of one kind of project data stands in the worker. */
export enum ProjectDataDownloadStatus {
  /** Stored: the project can show it. */
  downloaded = 'downloaded',
  /** A download runs in this worker session. */
  downloading = 'downloading',
  /** Neither: the worker just started the download again (or cannot). */
  idle = 'idle',
}

export type ProjectDataDownloadProgress = Partial<Record<ProjectDataKey, ProjectDataDownloadStatus>>;

/** Why the worker could not download the project's data, when the page should know it. */
export enum ProjectDataDownloadFailure {
  /** The API refused the project's data: the user has no access to the project (any more). */
  noProjectAccess = 'no-project-access',
}

export function isNoProjectAccessFailure(failure: unknown): boolean {
  return failure === ProjectDataDownloadFailure.noProjectAccess;
}

export function isProjectDataDownloaded(status: ProjectDataDownloadStatus | undefined): boolean {
  return status === ProjectDataDownloadStatus.downloaded;
}

export function isProjectDataDownloadIdle(status: ProjectDataDownloadStatus | undefined): boolean {
  return status === ProjectDataDownloadStatus.idle;
}
